import React from "react";
import ReactDOM from "react-dom/client";
import "@/index.css";
import "@/i18n";
import App from "@/App";

// ───────────────────────────────────────────────────────────────────────────
// ResizeObserver noise suppression
//
// Chrome (and `react-error-overlay`) treat the spec-defined "ResizeObserver
// loop completed with undelivered notifications" message as an uncaught
// error and stack it on screen during local development. It is *not* a
// real error — it just means a ResizeObserver callback caused another
// layout change in the same tick, which the spec explicitly allows.
//
// We stopImmediatePropagation on this specific message so the dev overlay
// never sees it. Real errors keep bubbling.
// Refs: https://github.com/WICG/resize-observer/issues/38
// ───────────────────────────────────────────────────────────────────────────
const RO_NEEDLE = "ResizeObserver loop";
window.addEventListener(
  "error",
  (e) => {
    if (e && typeof e.message === "string" && e.message.includes(RO_NEEDLE)) {
      e.stopImmediatePropagation();
      e.preventDefault();
    }
  },
  true,
);
window.addEventListener(
  "unhandledrejection",
  (e) => {
    const msg = (e && e.reason && (e.reason.message || String(e.reason))) || "";
    if (msg.includes(RO_NEEDLE)) {
      e.stopImmediatePropagation();
      e.preventDefault();
    }
  },
  true,
);

// react-error-overlay (CRA dev only) listens to `console.error` to surface
// errors in its red full-screen overlay. We patch console.error so the
// ResizeObserver-loop line never reaches it — but every other error still
// goes through unaltered.
const _origConsoleError = window.console.error.bind(window.console);
window.console.error = (...args) => {
  const first = args && args[0];
  if (typeof first === "string" && first.includes(RO_NEEDLE)) return;
  if (first && first.message && first.message.includes && first.message.includes(RO_NEEDLE)) return;
  _origConsoleError(...args);
};

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);
